import {type Issue} from '../../../state/issues/types';

const isStateFailure = (runState: string) => {
  return runState === 'failed' || runState === 'crashed';
};

type ActionMeta = {
  description?: string;
  resolution?: string;
  priority: number;
};

type IssueWithContent = Issue & {
  updatedDescription?: string;
  resolution?: string;
  priority: number;
};

type MatchByIssueKey = Record<string, (isFailure?: boolean) => ActionMeta>;
const matchByIssueKey: MatchByIssueKey = {
  epimetheus_needsdraintriage: () => ({
    description:
      'CoreWeave is taking automatic action to resolve this issue. This issue indicates conditions that require the node to be immediately replaced, and will cause the job to fail. You can restart your job and the issue will be cleared when the affected nodes are replaced.',
    priority: 100,
  }),
  epimetheus_criticalfault: () => ({
    description:
      'CoreWeave is taking automatic action to resolve this issue. This issue indicates conditions that require the node to be immediately rebooted, and will cause the job to fail. You can restart your job and the issue will be cleared when the affected nodes are rebooted.',
    priority: 80,
  }),
  epimetheus_needstriage: (isFailure?: boolean) => ({
    description: `CoreWeave is taking automatic action to resolve this issue. This issue indicates conditions that require the node to be replaced, and ${
      isFailure
        ? 'may have caused a failure'
        : 'might result in poor performance'
    }.`,
    resolution: isFailure
      ? 'You can restart your job and the issue will be cleared when the affected nodes are replaced.'
      : 'If you are noticing problems with your job you can checkpoint and restart it and the affected node will be replaced, clearing the problem.',
    priority: 60,
  }),
  epimetheus_gpufault: (isFailure?: boolean) => ({
    description: `CoreWeave is taking automatic action to resolve this issue. This issue indicates conditions that require the node to be replaced, and might result ${
      isFailure ? 'have caused a failure' : 'result in poor performance'
    }`,
    resolution: isFailure
      ? 'You can restart your job and the issue will be cleared when the affected nodes are replaced.'
      : 'If you are noticing problems with your job you can checkpoint and restart it and the affected node will be replaced, clearing the problem.',
    priority: 40,
  }),
  epimetheus_networkfault: (isFailure?: boolean) => ({
    description: `CoreWeave is taking automatic action to resolve this issue. This issue indicates conditions that require the node to be replaced, and might result ${
      isFailure ? 'have caused a failure' : 'result in poor performance'
    }`,
    resolution: isFailure
      ? 'You can restart your job and the issue will be cleared when the affected nodes are replaced.'
      : 'If you are noticing problems with your job you can checkpoint and restart it and the affected node will be replaced, clearing the problem.',
    priority: 20,
  }),
  default: (isFailure?: boolean) => ({
    description: `CoreWeave typically monitors this issue. However, this issue indicates conditions that might ${
      isFailure ? 'have caused a failure' : 'result in poor performance'
    }.`,
    priority: 0,
  }),
};

const keysByPriority = Object.keys(matchByIssueKey)
  .sort((a, b) => {
    return matchByIssueKey[b]().priority - matchByIssueKey[a]().priority;
  })
  .map(k => k.toLowerCase());

const annotateIssue = (issue: Issue, isFailure: boolean): IssueWithContent => {
  const lowercaseLabels = Object.keys(issue.labels ?? {}).map(k =>
    k.toLowerCase()
  );
  const match = keysByPriority.find(key => lowercaseLabels.includes(key));
  const matchKey = match || 'default';
  const {description, resolution, priority} =
    matchByIssueKey[matchKey](isFailure);

  return {
    ...issue,
    updatedDescription: description,
    resolution: resolution ?? '',
    priority,
  };
};

// build a priority issue rules engine
// source: https://docs.google.com/document/d/1PLA7ii_lA-KbTjlUprGDAmGlsLjsSDw29-i3PqmMrPA/edit?tab=t.0
// alerts: https://docs.google.com/spreadsheets/d/1hqRiyQRXvJ03e0XZDZsADLmBrPWmpLja7Js-SMErGKk/edit?gid=1955329917#gid=1955329917
export const getPriorityIssues = (
  issues: Issue[],
  runState: string
): IssueWithContent[] => {
  const isFailure = isStateFailure(runState);

  // Find the highest priority issue using reduce
  const highestPriorityIssue = issues.reduce<IssueWithContent | null>(
    (acc, issue) => {
      const annotated = annotateIssue(issue, isFailure);

      if (!acc) {
        return annotated;
      }

      // If priorities are equal, prefer the issue with lower timestamp (earlier)
      if (annotated.priority === acc.priority) {
        return annotated.timestamp < acc.timestamp ? annotated : acc;
      }

      // Otherwise return the issue with higher priority
      return annotated.priority > acc.priority ? annotated : acc;
    },
    null
  );

  return highestPriorityIssue ? [highestPriorityIssue] : [];
};
